import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useSession } from "../auth/session";
import { tenantPath } from "../config";

// Route guard: no protected content is visible pre-auth (E1 US-1.1). On an
// expired/absent session the user is redirected to /login with a returnTo so
// they land back where they were after signing in. When the session ended via a
// 401 (expired token/session), `reason=session_expired` rides along so the login
// page can explain why with the expiry banner — distinguishing it from a plain
// "you need to sign in" bounce.
//
// THE BOUNCE NAMES THE ORGANISATION, and that is task 09e495b1's correction of a bare `/login` here.
// Production serves every tenant from one apex host (ADR-0018) and the shell this guard wraps is mounted
// at `/` alone, so an in-app URL carries no slug: a bounce that forwarded the path alone left the sign-in
// page with no organisation to brand and — the report the owner made from production on 3.11.2026 — an
// email-link request that answered "Organisaatiota ei löytynyt" to the address they typed into it. The
// slug therefore comes from the URL where there is one and otherwise from the session that just ended
// (`lastTenantSlug`, ../auth/session.tsx), which is the only remaining record of which customer this
// document was working in. Both are the same rule as `lib/api`'s `tenantHint`, in the same order.
//
// It changes nothing for a person who signed in on the tenant-less door (the Incedo consultant, ADR-0018
// §6.1): their session names no tenant, so the bounce is the bare `/login` it has always been.
export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, expired, lastTenantSlug } = useSession();
  const location = useLocation();

  if (!session) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    const reason = expired ? "&reason=session_expired" : "";
    const to = tenantPath(`/login?returnTo=${returnTo}${reason}`, window.location, lastTenantSlug);
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}
